<script setup lang="ts">
import { computed, ref } from "vue";
import { ChevronDown, ExternalLink, RefreshCw } from "lucide-vue-next";
import QrcodeVue from "qrcode.vue";
import type { ExternalTotpEnrollment } from "@/services/firebase";

const props = withDefaults(
  defineProps<{
    enrollment: ExternalTotpEnrollment;
    busy?: boolean;
    error?: string;
    actionLabel?: string;
  }>(),
  {
    busy: false,
    error: "",
    actionLabel: "Verify authenticator app",
  },
);

const emit = defineEmits<{
  verify: [code: string];
  cancel: [];
}>();

const verificationCode = ref("");
const normalizedCode = computed(() =>
  verificationCode.value.replace(/\s/g, ""),
);

function submit() {
  if (/^\d{6}$/.test(normalizedCode.value) && !props.busy) {
    emit("verify", normalizedCode.value);
  }
}
</script>

<template>
  <form class="enrollment" @submit.prevent="submit">
    <div class="enrollment-heading">
      <h3>Set up your authenticator app</h3>
      <p>
        This security step is required for external accounts. No phone number is
        needed.
      </p>
    </div>

    <section class="app-options" aria-labelledby="authenticator-apps-title">
      <div>
        <h4 id="authenticator-apps-title">Need an authenticator app?</h4>
        <p>Install either app on your phone, then return here.</p>
      </div>
      <div class="app-list">
        <div class="app-row">
          <strong>Google Authenticator</strong>
          <span>
            <a
              href="https://apps.apple.com/app/google-authenticator/id388497605"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download Google Authenticator for iPhone or iPad"
              >iPhone or iPad <ExternalLink :size="13" aria-hidden="true"
            /></a>
            <a
              href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download Google Authenticator for Android"
              >Android <ExternalLink :size="13" aria-hidden="true"
            /></a>
          </span>
        </div>
        <div class="app-row">
          <strong>Microsoft Authenticator</strong>
          <span>
            <a
              href="https://apps.apple.com/app/microsoft-authenticator/id983156458"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download Microsoft Authenticator for iPhone or iPad"
              >iPhone or iPad <ExternalLink :size="13" aria-hidden="true"
            /></a>
            <a
              href="https://play.google.com/store/apps/details?id=com.azure.authenticator"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download Microsoft Authenticator for Android"
              >Android <ExternalLink :size="13" aria-hidden="true"
            /></a>
          </span>
        </div>
      </div>
    </section>

    <section class="scan-step" aria-labelledby="scan-title">
      <div>
        <h4 id="scan-title">Scan this QR code</h4>
        <p>
          In your authenticator app, add an account and scan the code shown
          here.
        </p>
      </div>
      <figure
        class="qr-code"
        aria-label="QR code for adding VIPdesk Project Planner to your authenticator app"
      >
        <QrcodeVue
          :value="enrollment.authenticatorUri"
          :size="208"
          level="M"
          render-as="svg"
          background="#ffffff"
          foreground="#0d2332"
          role="img"
          aria-label="Scan to add VIPdesk Project Planner to your authenticator app"
        />
      </figure>
      <a class="same-device-link" :href="enrollment.authenticatorUri">
        Open in an authenticator app on this device
      </a>
      <details class="manual-setup">
        <summary>
          Can’t scan the QR code?
          <ChevronDown class="disclosure-icon" :size="17" aria-hidden="true" />
        </summary>
        <p>Enter this setup key manually in your authenticator app:</p>
        <code>{{ enrollment.secretKey }}</code>
      </details>
    </section>

    <div class="field code-field">
      <label for="mfa-enrollment-code">Enter the 6-digit code</label>
      <input
        id="mfa-enrollment-code"
        v-model="verificationCode"
        class="input"
        type="text"
        inputmode="numeric"
        autocomplete="one-time-code"
        pattern="[0-9]{6}"
        maxlength="6"
        required
      />
      <p>Use the current code shown in your authenticator app.</p>
    </div>

    <p v-if="error" class="enrollment-error" role="alert">{{ error }}</p>

    <div class="enrollment-actions">
      <button
        class="button button--secondary"
        type="button"
        :disabled="busy"
        @click="emit('cancel')"
      >
        Back
      </button>
      <button
        class="button button--primary"
        type="submit"
        :disabled="busy || !/^\d{6}$/.test(normalizedCode)"
      >
        <RefreshCw v-if="busy" class="spin" :size="16" aria-hidden="true" />
        {{ busy ? "Verifying…" : actionLabel }}
      </button>
    </div>
  </form>
</template>

<style scoped>
.enrollment {
  display: grid;
  gap: 20px;
}
.enrollment-heading p,
.app-options p,
.scan-step p,
.code-field p,
.manual-setup p {
  margin-top: 5px;
  color: var(--text-soft);
}
.app-options,
.scan-step {
  display: grid;
  gap: 12px;
  padding-top: 18px;
  border-top: 1px solid var(--rule);
}
.app-list {
  display: grid;
  border: 1px solid var(--rule);
  border-radius: var(--radius-sm);
  background: var(--sheet-subtle);
}
.app-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 11px 12px;
}
.app-row + .app-row {
  border-top: 1px solid var(--rule);
}
.app-row > span {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 12px;
}
.app-row a,
.same-device-link {
  min-height: 32px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--brand-strong);
  font-size: 13px;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.qr-code {
  width: fit-content;
  max-width: 100%;
  margin: 0 auto;
  padding: 12px;
  overflow: hidden;
  border: 1px solid var(--rule-strong);
  border-radius: var(--radius-sm);
  background: #fff;
}
.qr-code :deep(svg) {
  display: block;
  max-width: 100%;
  height: auto;
}
.same-device-link {
  justify-self: center;
  text-align: center;
}
.manual-setup {
  padding: 10px 12px;
  border: 1px solid var(--rule);
  border-radius: var(--radius-sm);
  background: var(--sheet-subtle);
}
.manual-setup summary {
  min-height: 32px;
  display: flex;
  align-items: center;
  color: var(--text);
  font-weight: 700;
  cursor: pointer;
}
.disclosure-icon {
  margin-left: auto;
  transition: transform 150ms ease;
}
.manual-setup[open] .disclosure-icon {
  transform: rotate(180deg);
}
.manual-setup code {
  display: block;
  margin-top: 8px;
  overflow-wrap: anywhere;
  color: var(--text);
  font-family: "Segoe UI", system-ui, sans-serif;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.08em;
  user-select: all;
}
.code-field {
  max-width: 15rem;
}
.code-field .input {
  min-height: 44px;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.18em;
}
.code-field p {
  font-size: 12px;
}
.enrollment-error {
  padding: 11px 12px;
  border-radius: var(--radius-sm);
  color: var(--danger);
  background: var(--danger-soft);
}
.enrollment-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}
.spin {
  animation: spin 900ms linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
@media (max-width: 520px) {
  .app-row {
    align-items: flex-start;
    flex-direction: column;
  }
  .app-row > span {
    justify-content: flex-start;
  }
  .app-row a,
  .same-device-link,
  .manual-setup summary {
    min-height: 44px;
  }
  .code-field {
    max-width: none;
  }
  .enrollment-actions {
    flex-direction: column-reverse;
  }
  .enrollment-actions .button {
    width: 100%;
    min-height: 44px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .disclosure-icon,
  .spin {
    transition: none;
    animation: none;
  }
}
</style>
